// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import cartLineSharedStyles from "@keller/App/SharedStyle/Cart/CartLineSharedStyles";
import Minus from "@keller/XDL/Icons/Minus";
import Plus from "@keller/XDL/Icons/Plus";
import { color, maxLengthAttr, mergeWithCss, xMask } from "@keller/XDL/Utilities";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: TextFieldPresentationProps;
    showActions?: boolean;
    extendedActionStyles?: ProductQuantityOrderedActionsStyles;
}

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

export const productQuantityOrderedStyles: TextFieldPresentationProps = {
    ...cartLineSharedStyles.quantityTextField,
};

// XNG-Changes
export interface ProductQuantityOrderedActionsStyles {
    wrapper?: InjectableCss;
    actionClickable?: ClickableProps;
    quantityOrderedTextField?: TextFieldPresentationProps;
}

export const productQuantityOrderedActionsStyles: ProductQuantityOrderedActionsStyles = {
    wrapper: {
        css: css`
            background: ${color("common.white")};
            border: 1px solid ${color("common.border")};
            border-radius: 2px;
            display: flex;
            align-items: center;
            max-width: 102px;
        `,
    },
    actionClickable: {
        css: css`
            height: 26px;
            padding: 0 10px;
            display: flex;
            align-items: center;
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;

            ${IconWrapper} {
                color: ${color("common.ui5")};
            }
            &:hover {
                background: ${color("common.ui5")};
                ${IconWrapper} {
                    color: ${color("common.white")};
                }
            }
            &:focus {
                outline: none;
            }
        `,
    },
    quantityOrderedTextField: {
        cssOverrides: {
            formField: css`
                &&&& input {
                    &,
                    &:focus {
                        outline: none;
                        padding: 0;
                    }
                }
            `,
            inputSelect: css`
                height: 26px;
                border: none;
                font-size: 14px;
                letter-spacing: 0;
                line-height: 17px;
                text-align: center;
            `,
        },
    },
};

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        product: { id: productId },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    showActions,
    extendedActionStyles,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeWithCss(productQuantityOrderedStyles, extendedStyles));
    const [actionsStyles] = useState(() => mergeWithCss(productQuantityOrderedActionsStyles, extendedActionStyles));
    const [productQty, setProductQty] = useState(qtyOrdered.toString());

    useEffect(() => {
        if (qtyOrdered === Number(productQty)) {
            return;
        }
        setProductQty(`${qtyOrdered || ""}`);
    }, [qtyOrdered]);

    if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
        return null;
    }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setProductQty(value);

        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }

        const possibleNumber = Number(value);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChanged(possibleNumber);
        }
    };

    // XNG-Changes: apply qty mask for value
    const xChangeHandler = (event: any) => {
        event.target.value = Number(xMask.qty(event.target.value));
        qtyChangeHandler(event);
    };

    const updateQtyByAction = (type: "increase" | "decrease ") => {
        const event = { target: { value: "" } };
        event.target.value = `${Number(productQty) + (type === "increase" ? 1 : -1)}`;
        xChangeHandler(event);
    };

    const qtyInputProps = {
        label: labelOverride ?? translate("QTY"),
        value: productQty,
        type: "text",
        min: 0,
        max: 999999,
        maxLength: maxLengthAttr.qty,
        "data-test-selector": "product_qtyOrdered",
        ...otherProps,
        onKeyPress: keyPressHandler,
        onChange: xChangeHandler,
    };

    if (showActions) {
        return (
            <StyledWrapper {...actionsStyles.wrapper}>
                <Clickable
                    {...actionsStyles.actionClickable}
                    disabled={Number(productQty) <= 1}
                    onClick={() => updateQtyByAction("decrease ")}
                    data-test-selector="product_qtyOrdered_decrease"
                >
                    <Icon src={Minus} size={10} />
                </Clickable>
                <TextField {...qtyInputProps} {...actionsStyles.quantityOrderedTextField} />
                <Clickable
                    {...actionsStyles.actionClickable}
                    disabled={Number(productQty) > 999999}
                    onClick={() => updateQtyByAction("increase")}
                    data-test-selector="product_qtyOrdered_increase"
                >
                    <Icon src={Plus} size={10} />
                </Clickable>
            </StyledWrapper>
        );
    }

    return <TextField {...qtyInputProps} {...styles} />;
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));
